import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  assignOrder,
  exportOrder,
  getOrderArticlesStock,
  getOrderById,
  getOrderEmailHistory,
  getPdfVerification,
} from "../../app/services/orderService";
import { checkStockStatus } from "../../app/services/articleService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Col,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";

import "./view.scss";
import {
  FilePdf,
  PersonFillLock,
} from "react-bootstrap-icons";
import { Article, defaultArticle } from "../../models/responses/article";
import { defaultOrder, Order } from "../../models/responses/order";
import { getOrderStatusById } from "../../app/services/orderStatusService";
import {
  setClientToggleLocked,
  setClientType,
} from "../../app/slices/clientTypeSlice";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Supplier } from "../../models/responses/supplier";
import { getSuppliers } from "../../app/services/supplierService";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import StandardPriceProposal from "../../components/standardPriceProposal/standardPriceProposal";
import FleetPriceProposal from "../../components/fleetPriceProposal/fleetPriceProposal";
import CustomerInfoModal from "../../components/orders/customerInfoModal";
import ArticlesTable from "../../components/orders/articlesTable";
import OrderFields from "../../components/orders/orderFields";
import ArticleModal from "../../components/orders/articleModal";
import OrderHistoryAccordion from "../../components/orders/orderHistoryAccordion";

interface Props { }

const OrderView: FC<Props> = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { orderStatusId } = useParams<{ orderStatusId: string }>();

  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const dispatch = useAppDispatch();
  const order = useSelector((state: RootState) => state.order.order);
  const orderStatus = useSelector((state: RootState) => state.orderStatus.orderStatus);
  const clientType = useSelector((state: RootState) => state.clientType.clientType);
  const suppliers: Supplier[] = useSelector((state: RootState) => state.supplier.supplierList);
  const emailHistory = useSelector((state: RootState) => state.order.emailHistory);
  const loadingOrders: boolean = useSelector((state: RootState) => state.order.isWaitingOrderGetResponse);
  const pdfVerification = useSelector((state: RootState) => state.order.pdfVerification);
  const isWaitingOrderExportResponse = useSelector((state: RootState) => state.order.isWaitingOrderExportResponse);
  const isWaitingSendOrderEmailResponse = useSelector((state: RootState) => state.order.isWaitingSendOrderEmailResponse);
  const isWaitingPriceProposalPreviewResponse = useSelector((state: RootState) => state.order.isWaitingPriceProposalPreviewResponse);
  const isWaitingStockResponse = useSelector((state: RootState) => state.order.isWaitingStockResponse);

  const [orderData, setOrderData] = useState<Order>({ ...defaultOrder });
  const [modalArticle, setModalArticle] = useState<Article>({ ...defaultArticle });
  const [articleStockStatus, setArticleStockStatus] = useState<Record<string, any>>({});
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showStockInfo, setShowStockInfo] = useState(false);
  const [showStockErrorModal, setShowStockErrorModal] = useState(false);
  const [showExportModal, setShowExportModel] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [allProductVerified, setAllProductVerified] = useState(false);
  const [allTimeVerified, setAllTimeVerified] = useState(false);
  const [allowedToExport, setAllowedToExport] = useState(false);
  const [supplierExists, setSupplierExists] = useState(false);
  const [displayError, setDisplayError] = useState(false);

  useEffect(() => {
    dispatch(setClientToggleLocked(true));
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [orderStatusId, account, accounts, dispatch, instance]);

  useEffect(() => { }, [orderStatus]);

  useEffect(() => {
    dispatch(getOrderById(instance, account, orderId));
  }, [account, accounts, dispatch, instance, orderId]);

  useEffect(() => {
    if (order && order.id) {
      setOrderData({ ...order });
      if (clientType !== order.clientType) {
        dispatch(setClientType(order.clientType));
      }
    }
  }, [order, clientType, dispatch]);

  useEffect(() => {
    const productArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Product,
      ) ?? [];
    const timeArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Time,
      ) ?? [];
    setAllProductVerified(
      productArticles.length === 0 ||
      productArticles.every((art) => art.verified),
    );
    setAllTimeVerified(
      timeArticles.length === 0 || timeArticles.every((art) => art.verified),
    );
  }, [order, orderData.articles]);

  useEffect(() => {
    if (!order) return;
    let allowed = true;
    if (!orderData) return;
    if (!orderData.articles || orderData.articles.length <= 0) allowed = false;
    if (!allProductVerified || !allTimeVerified) allowed = false;
    if (!orderData.registrationNumber || orderData.registrationNumber === "")
      allowed = false;
    if (orderData.clientType === ClientType.Standard) {
      if (!orderData.aoNumber || orderData.aoNumber === "") allowed = false;
    } else if (orderData.clientType === ClientType.Catalog) {
      if (!orderData.externalOrderId) allowed = false;
    }
    if (!supplierExists) allowed = false;
    setAllowedToExport(allowed);
  }, [
    order,
    orderData,
    orderData.articles,
    allProductVerified,
    allTimeVerified,
    supplierExists,
  ]);

  useEffect(() => {
    if (!order) return;
    let exists = true;
    if (order.clientType !== ClientType.Fleet && !orderData.supplierId) {
      if (!orderData.articles) exists = false;
      else {
        const productArticles = orderData.articles.filter(
          (art) => art.articleType === ArticleType.Product,
        );
        const allHaveSupplier = productArticles.every((art) => !!art.supplierId);
        if (!allHaveSupplier) exists = false;
      }
    }
    setSupplierExists(exists);
  }, [order, orderData, orderData.articles]);

  useEffect(() => {
    setModalArticle((prev) => ({
      ...prev,
      totalExVat: parseFloat(
        (parseFloat(prev.salesPrice) * prev.quantity).toFixed(2),
      ),
    }));
  }, [modalArticle.quantity, modalArticle.salesPrice, modalArticle.vat]);

  useEffect(() => {
    dispatch(getSuppliers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getOrderEmailHistory(instance, account, orderId));
  }, [account, accounts, dispatch, instance, orderId]);

  useEffect(() => {
    if (pdfVerification) {
      const blob = new Blob([pdfVerification], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "test_file.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }, [pdfVerification]);

  const openArticleModalHandler = (article: Article) => {
    setModalArticle({ ...modalArticle, ...article });
    setShowArticleModal(true);
  };

  const closeArticleModalHandler = () => {
    setShowArticleModal(false);
  };

  const handleShowCustomerModal = () => setShowCustomerModal(true);
  const handleCloseCustomerModal = () => setShowCustomerModal(false);

  const handleShowExportModal = () => setShowExportModel(true);
  const handleCloseExportModal = () => setShowExportModel(false);

  const handleExportOrder = async () => {
    setDisplayError(false);
    setShowExportModel(false);
    if (clientType === ClientType.Fleet) {
      await dispatch(getOrderArticlesStock(instance, account, orderId));
    }
    await dispatch(
      exportOrder(instance, account, orderId, handleExportOrderSuccess),
    );
  };

  const handleExportOrderSuccess = async (
    orderId: string | null,
    isUnprocessableEntity?: boolean,
  ) => {
    if (isUnprocessableEntity && orderData.articles) {
      setShowStockErrorModal(true);
    } else {
      setShowStockInfo(false);
      setArticleStockStatus({});
      if (orderId) {
        setOrderData((prev) => ({ ...prev, exported: true }));
      }
    }
  };

  const handleConfirmStockError = async () => {
    setShowStockErrorModal(false);
    setShowStockInfo(true);
    if (orderData.articles) {
      const articlesWithId = orderData.articles.filter(
        (art) => typeof art.id === "string" && art.id,
      );
      const results = await Promise.all(
        articlesWithId.map(async (art) => {
          try {
            const result: any = await dispatch(
              checkStockStatus(instance, account, art.id as string),
            );
            const payload =
              result && typeof result === "object" && "payload" in result
                ? result.payload
                : result;
            return [art.id, payload];
          } catch (e) {
            return [art.id, { error: true }];
          }
        }),
      );
      setArticleStockStatus(Object.fromEntries(results));
    }
  };

  const handleAssignOrder = () => {
    if (account) {
      dispatch(assignOrder(instance, account, orderId));
      setOrderData({
        ...orderData,
        userId: account.localAccountId || "",
        userName: account.name || "",
      });
    }
  };

  const handleCheckStock = () => {
    dispatch(getOrderArticlesStock(instance, account, orderId));
  };

  const handlePdfVerification = () => {
    dispatch(getPdfVerification(instance, account, orderId));
  };

  return (
    <div>
      <Modal
        show={isWaitingOrderExportResponse}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" className="mb-2" />
          <div>Vänligen vänta, exporterar order...</div>
        </Modal.Body>
      </Modal>

      {loadingOrders ? (
        <>
          <Row>
            <Col className="text-middle">
              <Spinner variant="primary" />
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Breadcrumb>
            <Breadcrumb.Item href={`/orderStatus/${orderData.orderStatus?.id}`}>
              {orderData.orderStatus?.name}
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{orderData.customer?.name}</Breadcrumb.Item>
          </Breadcrumb>
          <hr />
          <OrderFields
            orderData={orderData}
            orderStatus={orderStatus}
            clientType={clientType}
            order={order}
            onShowCustomerModal={handleShowCustomerModal}
          />
          {orderData.articles && orderData.articles.length > 0 && (
            <ArticlesTable
              articles={orderData.articles}
              allProductVerified={allProductVerified}
              allTimeVerified={allTimeVerified}
              showStockInfo={showStockInfo}
              articleStockStatus={articleStockStatus}
              orderPageType={orderStatus.orderPageType}
              clientType={clientType}
              suppliers={suppliers}
              orderSupplierId={orderData.supplierId}
              onArticleClick={openArticleModalHandler}
            />
          )}
          <ArticleModal
            show={showArticleModal}
            article={modalArticle}
            clientType={clientType}
            articleStockStatus={articleStockStatus}
            onHide={closeArticleModalHandler}
          />
          <CustomerInfoModal
            show={showCustomerModal}
            customer={order.customer}
            onClose={handleCloseCustomerModal}
          />
          <Row>
            <div>
              {orderData.userId ? (
                <span className="float-end">
                  <Button
                    variant="primary"
                    href={`/orderStatus/${orderData.orderStatus?.id}/orders/edit/${orderData.id}`}
                  >
                    Lås upp <PersonFillLock />
                  </Button>
                  <span className="username-text">
                    Låst av: {orderData.userName}
                  </span>
                </span>
              ) : (
                <span className="float-end">
                  <Button
                    variant="primary"
                    className="me-2"
                    onClick={handleAssignOrder}
                  >
                    Lås order <PersonFillLock />
                  </Button>
                  <Button
                    variant="primary"
                    href={`/orderStatus/${orderData.orderStatus?.id}/orders/edit/${orderData.id}`}
                  >
                    Redigera
                  </Button>
                </span>
              )}
              {!orderData.exported && !isWaitingOrderExportResponse && (
                <>
                  {clientType === ClientType.Fleet && (
                    <>
                      <Button
                        variant="outline-primary"
                        className="float-end me-2"
                        onClick={handleCheckStock}
                        disabled={isWaitingStockResponse}
                      >
                        Hämta saldo
                      </Button>
                    </>
                  )}
                  <>
                    <Button
                      variant="primary"
                      className="float-end me-2"
                      onClick={handleShowExportModal}
                      disabled={
                        !allowedToExport || isWaitingOrderExportResponse
                      }
                    >
                      Exportera till AX
                    </Button>
                  </>
                  {!supplierExists && (
                    <Badge bg="danger float-end mt-2 me-3">
                      Leverantör krävs på antingen alla artiklar eller order för
                      export
                    </Badge>
                  )}
                  {(!allProductVerified || !allTimeVerified) && (
                    <Badge bg="danger float-end mt-2 me-3">
                      Alla artiklar måste klarmarkeras för export
                    </Badge>
                  )}
                </>
              )}
              {orderData.exported && (
                <>
                  <Button
                    variant="primary"
                    className="float-end me-2"
                    onClick={handleShowExportModal}
                    disabled={!allowedToExport || isWaitingOrderExportResponse}
                  >
                    Återexportera artiklar till AX
                  </Button>
                </>
              )}
              {orderData.exported &&
                orderData.clientType === ClientType.Standard && (
                  <>
                    <Button
                      variant="outline-primary"
                      className="float-end me-2"
                      onClick={handlePdfVerification}
                    >
                      Hämta pdf-bekräftelse <FilePdf />
                    </Button>
                  </>
                )}
              <Modal show={showExportModal} onHide={handleCloseExportModal}>
                <Modal.Header closeButton>
                  <Modal.Title>Exportera order till AX</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Är du säker på att du vill exportera ordern till AX?
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={handleCloseExportModal}>
                    Avbryt
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!allowedToExport || isWaitingOrderExportResponse}
                    onClick={handleExportOrder}
                  >
                    Exportera till AX
                  </Button>
                </Modal.Footer>
              </Modal>
              <Modal
                show={showStockErrorModal}
                onHide={() => setShowStockErrorModal(false)}
              >
                <Modal.Header closeButton>
                  <Modal.Title>Exportfel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Det går inte att exportera ordern eftersom någon artikel har
                  lägre saldo än antal. Kontrollera artiklarna och försök igen.
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="primary" onClick={handleConfirmStockError}>
                    OK
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Alert
              key="alert"
              variant="danger"
              show={displayError}
              className="mt-3"
            >
              <p>Något gick fel vid exporten till AX, vänligen försök igen.</p>
            </Alert>
          </Row>
          <hr />

          {orderStatus.orderPageType?.emailVerification && (
            <>
              {orderData.clientType === ClientType.Standard && (
                <StandardPriceProposal
                  instance={instance}
                  account={account}
                  orderId={orderId}
                  emailHistory={emailHistory}
                  isWaitingSendVerificationEmailResponse={
                    isWaitingSendOrderEmailResponse ?? false
                  }
                  isWaitingVerificationPreviewResponse={
                    isWaitingPriceProposalPreviewResponse ?? false
                  }
                  customer={orderData.customer}
                />
              )}
            </>
          )}
          {orderStatus.orderPageType?.emailPriceProposal && (
            <>
              {orderData.clientType === ClientType.Fleet && (
                <FleetPriceProposal
                  instance={instance}
                  account={account}
                  orderId={orderId}
                  emailHistory={emailHistory}
                  isWaitingSendOrderEmailResponse={
                    isWaitingSendOrderEmailResponse ?? false
                  }
                  isWaitingPriceProposalPreviewResponse={
                    isWaitingPriceProposalPreviewResponse ?? false
                  }
                />
              )}
            </>
          )}

          <hr />
          <OrderHistoryAccordion
            orderHistory={orderData?.orderHistory}
            orderId={orderId}
            instance={instance}
            account={account}
          />
        </>
      )}
    </div>
  );
};

export default OrderView;
